import * as React from "react";
import {
  Checkbox as MuiCheckbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  FormControlProps,
  CheckboxProps,
  FormGroupProps,
  FormControlLabelProps,
} from "@mui/material";
import { ChoiceT, StringChoiceT } from "@talentpair/types/entities/misc";
import { FieldPropsT } from "@talentpair/types/formik";
import { MarginT } from "@talentpair/types/misc";
import Field, { FieldWrapperPropsT } from "../Field";
import HasFocus from "../../components/HasFocus";
import { FormikChoiceT } from "../choiceFieldTypes";
import { createEventObject, EventObjectT } from "../../utils";
import PillButtonLabel from "./PillButtonLabel";

export interface CheckboxPropsT<V = boolean, F = boolean> extends FieldWrapperPropsT<V, F> {
  label?: React.ReactNode;
  fieldLabel?: React.ReactNode;
  CheckboxProps?: Partial<CheckboxProps>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormControlLabelComponent?: React.ComponentType<any>;
  // Although the ability to provide your own `FormControlLabelComponent` can suffice for providing
  // your own props, this gives rise to a strange bug where the checkbox doesn't issue `blur` events
  // after clicking the checkbox and then clicking away. For example, that was happening here:
  // https://goo.gl/PoLUeF, resulting in the "Candidate Submittals" label never losing its focus
  // highlight.
  FormControlLabelProps?: Partial<FormControlLabelProps>;
}
export const Checkbox = <V, F>({
  label,
  fieldLabel = null, // use this to have field label in addition to checkboxes' inline label
  CheckboxProps: checkboxProps = {},
  FormControlLabelComponent = FormControlLabel,
  FormControlLabelProps: formControlLabelProps = {},
  ...props
}: CheckboxPropsT<V, F>): React.ReactElement => (
  <Field
    {...props}
    label={fieldLabel}
    render={({ field: { value, ...field } }: FieldPropsT<V, F, Record<string, unknown>>) => (
      <FormControlLabelComponent
        checked={value || false}
        control={<MuiCheckbox {...checkboxProps} />}
        label={label || ""}
        {...formControlLabelProps}
        {...field}
      />
    )}
  />
);

export interface BaseCheckboxGroupPropsT<V = boolean, F = boolean>
  extends Omit<FieldWrapperPropsT<V, F>, "name"> {
  fullWidth?: boolean;
  label?: React.ReactNode;
  choices: Array<StringChoiceT | ChoiceT>;
  margin?: MarginT;
  renderLabel?: (value: FormikChoiceT) => React.ReactNode;
  FormGroupProps?: FormGroupProps;
}

const BaseCheckboxGroup = <V, F>({
  fullWidth = false,
  label = null,
  margin = "normal",
  choices,
  FormGroupProps: formGroupProps = {},
  renderLabel,
  ...props
}: BaseCheckboxGroupPropsT<V, F>): React.ReactElement => (
  <FormControl component="fieldset" fullWidth={fullWidth} margin={margin} variant="standard">
    {label && <FormLabel component="legend">{label}</FormLabel>}
    <FormGroup row {...formGroupProps}>
      {choices.map((c: StringChoiceT | ChoiceT) => (
        <Checkbox key={c.id} label={c.name} name={String(c.id)} margin="none" {...props} />
      ))}
    </FormGroup>
  </FormControl>
);
export interface BaseCheckboxListPropsT<V, F> extends FieldWrapperPropsT<V, F> {
  choices: FormikChoiceT[];
  disabledChoices?: FormikChoiceT[] | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormControlLabelComponent?: React.ComponentType<any>;
  FormControlProps?: Partial<FormControlProps>;
  FormControlLabelProps?: Partial<FormControlLabelProps>;
  CheckboxProps?: Partial<CheckboxProps>;
  FormGroupProps?: Partial<FormGroupProps>;
  FocusProps?: Record<string, unknown>;
  group?: boolean;
  renderLabel?: null | ((value: FormikChoiceT) => React.ReactNode);
}
function BaseCheckboxList<V extends Array<unknown>, F extends FormikChoiceT["id"][]>({
  choices,
  disabledChoices,
  renderLabel = null,
  FocusProps = {},
  FormGroupProps: formGroupProps = {},
  FormControlLabelComponent = FormControlLabel,
  FormControlLabelProps: formControlLabelProps = {},
  FormControlProps: formControlProps = {},
  CheckboxProps: checkboxProps = {},
  group = true,
  ...props
}: BaseCheckboxListPropsT<V, F>): React.ReactElement | null {
  if (FormControlLabelComponent == null) return null;
  return (
    <HasFocus
      {...FocusProps}
      render={(hasFocus): React.ReactNode => (
        <Field
          group={group}
          hasFocus={hasFocus}
          {...props}
          render={({ field: { value, onChange } }) => (
            <FormGroup row {...formGroupProps}>
              {choices.map((c) => (
                <FormControl
                  key={String(c.id)}
                  margin="none"
                  variant="standard"
                  {...formControlProps}
                >
                  <FormControlLabelComponent
                    disabled={
                      !!value &&
                      Array.isArray(disabledChoices) &&
                      !!disabledChoices.find(({ id }) => id === c.id)
                    }
                    checked={value ? value.includes(c.id) : false}
                    label={renderLabel ? renderLabel(c) : c.name}
                    control={
                      <MuiCheckbox
                        // @ts-expect-error - oh well
                        name={c.name}
                        // @ts-expect-error - 2322 - mismatch between Field's EventObjectT and mui's onChange type
                        onChange={(e: EventObjectT<F>, checked: boolean): Promise<unknown> =>
                          onChange(
                            createEventObject<F>(
                              // @ts-expect-error - 2345 - mismatch between string | number | null and the generic F
                              value
                                ? checked
                                  ? [...value, c.id]
                                  : value.filter((v: FormikChoiceT["id"]) => v !== c.id)
                                : [c.id],
                            ),
                          )
                        }
                        {...checkboxProps}
                      />
                    }
                    {...formControlLabelProps}
                  />
                </FormControl>
              ))}
            </FormGroup>
          )}
        />
      )}
    />
  );
}

const checkboxPillProps = {
  fullWidth: true,
  CheckboxProps: { classes: { root: "hide" } },
  FormControlLabelComponent: PillButtonLabel,
  FormControlProps: { classes: { root: "flex-auto col-12 lg-col-6 pt3 px2" } },
  FormGroupProps: { className: "mxn2" },
};

interface CheckboxGroupPropsT<V, F> extends BaseCheckboxGroupPropsT<V, F> {
  pill?: boolean;
}
export interface CheckboxListPropsT<V, F> extends BaseCheckboxListPropsT<V, F> {
  pill?: boolean;
}

// Checkbox group is for a "list" of choices which are stored as single boolean values but handled here as a list of choices
// { prop1: true, prop2: false, prop3: true }
export const CheckboxGroup = <V, F>({
  pill = false,
  choices,
  ...props
}: CheckboxGroupPropsT<V, F>): React.ReactElement => (
  <BaseCheckboxGroup choices={choices} {...(pill ? checkboxPillProps : null)} {...props} />
);

// CheckboxList handles lists of choices which are stored as a single property (as opposed to a flattened list of boolean values)
// { property: [{choice}, {choice}, {choice}] }
export const CheckboxList = <V extends Array<unknown>, F extends Array<string | number>>({
  pill = false,
  choices,
  ...props
}: CheckboxListPropsT<V, F>): React.ReactElement =>
  pill ? (
    <BaseCheckboxList {...checkboxPillProps} className="mtn3" choices={choices} {...props} />
  ) : (
    <BaseCheckboxList choices={choices} {...props} />
  );

export const _test = { BaseCheckboxGroup, BaseCheckboxList };
